import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Zap, FileText, GanttChartSquare, ListChecks } from "lucide-react";
import TaskTree from "@/components/TaskTree";
import TaskDialog from "@/components/TaskDialog";
import GanttChart from "@/components/GanttChart";
import { PROJECT_STATUSES, statusMeta } from "@/lib/taskman";

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState({ team: [], client_users: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentTask, setParentTask] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}`);
      setProject(data.project);
      setTasks(data.tasks || []);
      const a = await api.get("/taskman/assignees", {
        params: { client_id: data.project.client_id } });
      setAssignees(a.data);
    } catch { toast.error("Failed to load project"); }
    finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const patchProject = async (updates) => {
    try {
      await api.patch(`/projects/${projectId}`, updates);
      setProject((p) => ({ ...p, ...updates }));
      toast.success("Project updated");
    } catch { toast.error("Update failed"); }
  };

  const saveTask = async (payload) => {
    try {
      if (editing?.task_id) {
        await api.patch(`/tasks/${editing.task_id}`, payload);
      } else {
        payload.project_id = projectId;
        if (parentTask) payload.parent_task_id = parentTask.task_id;
        await api.post("/tasks", payload);
      }
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const quickStatus = async (task, status) => {
    try {
      await api.patch(`/tasks/${task.task_id}`, { status });
      setTasks((rows) => rows.map((r) => r.task_id === task.task_id ? { ...r, status } : r));
    } catch { toast.error("Update failed"); }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}" and all its subtasks?`)) return;
    try {
      await api.delete(`/tasks/${task.task_id}`);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const reorder = async (task_ids) => {
    try { await api.post("/tasks/reorder", { task_ids }); load(); }
    catch { toast.error("Reorder failed"); }
  };

  const updateDates = async (taskId, dates) => {
    try {
      await api.patch(`/tasks/${taskId}`, dates);
      setTasks((rows) => rows.map((r) => r.task_id === taskId ? { ...r, ...dates } : r));
    } catch { toast.error("Date update failed"); }
  };

  if (loading) return <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>;
  if (!project) return <div className="p-10 text-center text-sm text-zinc-400">Project not found.</div>;

  const st = statusMeta(PROJECT_STATUSES, project.status);

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="project-detail-page">
      <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
        <ArrowLeft className="h-3.5 w-3.5" /> All projects
      </Link>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2"
            data-testid="project-detail-name">
            {project.name}
            {project.is_default && (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase">
                <Zap className="h-3 w-3" /> Do &amp; Charge
              </span>
            )}
          </h1>
          <div className="text-sm text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
            <span>{project.client_name}</span>
            {project.source_proposal_id && (
              <Link to={`/clients/${project.client_id}/proposals/${project.source_proposal_id}`}
                className="inline-flex items-center gap-1 text-violet-700 hover:underline text-xs">
                <FileText className="h-3 w-3" /> View source proposal
              </Link>
            )}
          </div>
          {project.description && (
            <p className="text-xs text-zinc-500 mt-2 max-w-2xl">{project.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Select value={project.execution_mode || "parallel"}
            onValueChange={(v) => patchProject({ execution_mode: v })}>
            <SelectTrigger className="w-52 h-8 text-xs" data-testid="project-exec-mode">
              <span className="truncate">
                Top-level tasks: {project.execution_mode === "sequential" ? "priority order" : "simultaneous"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="parallel">Run simultaneously</SelectItem>
              <SelectItem value="sequential">Run in priority order</SelectItem>
            </SelectContent>
          </Select>
          <Select value={project.status}
            onValueChange={(v) => patchProject({ status: v })}>
            <SelectTrigger className="w-36 h-8" data-testid="project-status-select">
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${st.cls}`}>{st.label}</span>
            </SelectTrigger>
            <SelectContent>
              {PROJECT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => { setEditing(null); setParentTask(null); setDialogOpen(true); }}
            data-testid="project-add-task-btn">
            <Plus className="h-4 w-4 mr-1" /> Add task
          </Button>
        </div>
      </div>

      <Tabs defaultValue="tasks">
        <TabsList>
          <TabsTrigger value="tasks" data-testid="project-tab-tasks">
            <ListChecks className="h-4 w-4 mr-1" /> Tasks ({tasks.length})
          </TabsTrigger>
          <TabsTrigger value="gantt" data-testid="project-tab-gantt">
            <GanttChartSquare className="h-4 w-4 mr-1" /> Gantt
          </TabsTrigger>
        </TabsList>
        <TabsContent value="tasks">
          <Card><CardContent className="p-4">
            <TaskTree tasks={tasks} rootSequential={project.execution_mode === "sequential"}
              onEdit={(t) => { setEditing(t); setParentTask(null); setDialogOpen(true); }}
              onAddSub={(t) => { setEditing(null); setParentTask(t); setDialogOpen(true); }}
              onDelete={deleteTask} onQuickStatus={quickStatus} onReorder={reorder} />
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="gantt">
          <Card><CardContent className="p-4">
            <p className="text-xs text-zinc-400 mb-3">
              Drag a bar to move it, drag its edges to resize. Dashed arrows show priority-order dependencies.
            </p>
            <GanttChart tasks={tasks} rootSequential={project.execution_mode === "sequential"}
              onUpdateDates={updateDates} />
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen}
        initial={editing} onSave={saveTask} assignees={assignees} />
    </div>
  );
}
